import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectSiteId } from '@store/selectors/site.selectors';
import { useSiteProfessions } from '@components/Pages/IssuesPage/hooks';
import type { IAdminTag } from '@interfaces/IAdminTag';
import { Button } from '@components/ui/Button/Button';
import { Modal } from '@components/ui/Modal/Modal';
import { Toasts } from '@components/ui/Toasts/Toasts';
import { useToasts } from '@components/ui/Toasts/useToasts';
import { FilterIcon, PlusIcon, SearchIcon } from '@/utils/icons';
import { filterTags, filterTagsBySearch, getInitialFixedFilterState, isTagAssigned, type IAdminPanelFilters } from './utils/tagFilters';
import { useAdminTagsStream } from './hooks/useAdminTagsStream';
import { useAssignTag, useUnassignTag } from './hooks/useAssignTag';
import { AssignTagDialog } from './AssignTagDialog/AssignTagDialog';
import { UnassignTagDialog } from './UnassignTagDialog/UnassignTagDialog';
import { InventoryFilterSidebar } from './InventoryFilterSidebar';
import { TagsTable } from './TagsTable/TagsTable';
import { ProfessionColorProvider } from './ProfessionColorContext';
import layout from '../AdminPage.module.css';
import { PageHeader } from '@components/ui/PageHeader/PageHeader';
import styles from './styles.module.css';

/**
 * Assign-dialog state: 
 * 1. `null` = closed, 
 * 2. `'toolbar'` = open with a free tag picker
 * 3. a tag object = open with that tag preselected and locked.
 */
type AssignDialogState = 'toolbar' | IAdminTag | null;

export function TagsTab() {
  const { t } = useTranslation();
  const siteId = useSelector(selectSiteId);
  const { tags, status, loaded } = useAdminTagsStream(siteId);

  const { data: professionsData } = useSiteProfessions(siteId ?? null);
  const professions = useMemo(() => professionsData?.professions ?? [], [professionsData]);

  const colorByProfessionId = useMemo(
    () => new Map(professions.map((p) => [p._id, p.backgroundColor ?? null])),
    [professions],
  );

  const { toasts, push, dismiss } = useToasts();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<IAdminPanelFilters>(getInitialFixedFilterState);
  const [assigning, setAssigning] = useState<AssignDialogState>(null);
  const [unassigning, setUnassigning] = useState<IAdminTag | null>(null);
  const [noTagsOpen, setNoTagsOpen] = useState(false);
  const assignMutation = useAssignTag(siteId ?? null, push);
  const unassignMutation = useUnassignTag(siteId ?? null, push);

  const unassignedTags = useMemo(() => tags.filter((tag) => !isTagAssigned(tag)), [tags]);

  function handleOpenAssign() {
    if (unassignedTags.length === 0) {
      setNoTagsOpen(true);
      return;
    }
    setAssigning('toolbar');
  }
  // Filters stay applied while the sidebar is hidden — the toggle only
  // collapses the panel, it does not reset the selection.
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const visibleTags = useMemo(
    () => filterTagsBySearch(filterTags(tags, filters), search),
    [tags, filters, search],
  );

  const liveLabel =
    status === 'open'
      ? t('adminTags.live.connected')
      : status === 'reconnecting'
        ? t('adminTags.live.reconnecting')
        : t('adminTags.live.connecting');

  return (
    <div className={layout.page}>
      <div className={layout.pageHeader}>
        <PageHeader
          title={t('adminTags.title')}
          subtitle={t('adminTags.subtitle')}
          badge={
            <span className={`${styles.liveBadge} ${status === 'open' ? styles.liveOn : styles.liveOff}`}>
              <span className={styles.liveDot} />
              {liveLabel}
            </span>
          }
        />
        <div className={layout.toolbar}>
          <div className={layout.searchWrap}>
            <SearchIcon size={15} />
            <input
              className={layout.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('adminTags.searchPlaceholder')}
            />
          </div>
          <button
            type="button"
            className={sidebarOpen ? `${styles.filterToggle} ${styles.filterToggleOn}` : styles.filterToggle}
            aria-pressed={sidebarOpen}
            aria-label={t(sidebarOpen ? 'adminTags.filters.hide' : 'adminTags.filters.show')}
            title={t(sidebarOpen ? 'adminTags.filters.hide' : 'adminTags.filters.show')}
            onClick={() => setSidebarOpen((open) => !open)}
          >
            <FilterIcon size={15} />
          </button>
          <span className={layout.count}>{t('adminTags.count', { count: visibleTags.length })}</span>
          <div className={layout.toolbarSpacer} />
          <Button variant="primary" size="sm" onClick={handleOpenAssign}>
            <PlusIcon size={14} />
            {t('adminTags.assign.title')}
          </Button>
        </div>
      </div>

      <div className={layout.body}>
        {sidebarOpen && (
          <InventoryFilterSidebar
            tags={tags}
            professions={professions}
            filters={filters}
            onChange={setFilters}
            onClear={() => setFilters(getInitialFixedFilterState())}
          />
        )}
        <ProfessionColorProvider value={colorByProfessionId}>
          <TagsTable
            tags={visibleTags}
            emptyMessage={loaded ? t('adminTags.empty') : t('adminTags.loading')}
            onAssign={(tag) => setAssigning(tag)}
            onEdit={(tag) => setAssigning(tag)}
            onUnassign={setUnassigning}
          />
        </ProfessionColorProvider>
      </div>

      {assigning !== null && siteId && (
        <AssignTagDialog
          key={assigning === 'toolbar' ? 'toolbar' : assigning.tagId}
          open
          tag={assigning === 'toolbar' ? null : assigning}
          unassignedTags={unassignedTags}
          professions={professions}
          siteId={siteId}
          submitting={assignMutation.isPending}
          onClose={() => setAssigning(null)}
          onSubmit={(draft) => {
            if (assignMutation.isPending) return;
            assignMutation.mutate(draft, { onSuccess: () => setAssigning(null) });
          }}
        />
      )}

      {unassigning && (
        <UnassignTagDialog
          key={unassigning.tagId}
          tag={unassigning}
          unassigning={unassignMutation.isPending}
          onConfirm={(tag) => {
            if (unassignMutation.isPending) return;
            unassignMutation.mutate(tag.tagId, { onSuccess: () => setUnassigning(null) });
          }}
          onClose={() => setUnassigning(null)}
        />
      )}

      <Modal
        open={noTagsOpen}
        onClose={() => setNoTagsOpen(false)}
        title={t('adminTags.assign.title')}
        footer={
          <Button variant="primary" size="md" onClick={() => setNoTagsOpen(false)}>
            {t('adminTags.assign.gotIt')}
          </Button>
        }
      >
        <p className={styles.noTagsText}>{t('adminTags.assign.noTagsAvailable')}</p>
      </Modal>

      <Toasts toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}